import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  ExecuteCancelDialog,
  type ExecuteCancelDialogProps,
} from '~/components/elements/ExecuteCancelDialog'
import {
  ItemSelector,
  type ItemSelectorProps,
} from '~/components/elements/ItemSelector'
import { type CustomerStrategyType } from '~/gql/graphql'
import { useFormField } from '~/hooks/useFormField'
import { useLogEvent } from '~/hooks/useLogEvent'
import { exists, findByPath } from '~/utils'

import { type InputValues } from '../utils'

type StrategyTypeItem = {
  id: CustomerStrategyType
  label: string
}

const useStrategyTypeData = (): StrategyTypeItem[] => {
  const { t } = useTranslation()

  return React.useMemo(
    () => [
      { id: 'new', label: t('new_customer_acquisition') },
      { id: 'existing', label: t('existing_customer_growth') },
      { id: 'free', label: t('free_input') },
    ],
    [t],
  )
}

export type StrategyTypeInputProps = {
  index: number
}

export const StrategyTypeInput = ({ index }: StrategyTypeInputProps) => {
  const { control, getValues, setValue } = useFormContext<InputValues>()
  const { t } = useTranslation()
  const data = useStrategyTypeData()
  const strategyTypeField = useFormField(
    control,
    `strategies.${index}.strategyType`,
  )

  const value = React.useMemo(
    () => findByPath(data, 'id', strategyTypeField.value),
    [data, strategyTypeField.value],
  )

  const [nextCustomerStrategyType, setNextCustomerStrategyType] =
    React.useState<CustomerStrategyType | undefined>(undefined)
  const logEvent = useLogEvent()
  const handleChange = React.useCallback<EH<ItemSelectorProps, 'onChange'>>(
    (item: StrategyTypeItem) => {
      const segmentRoutes = getValues(`strategies.${index}.segmentRoutes`)
      if (0 < segmentRoutes.length) {
        setNextCustomerStrategyType(item.id)
      } else {
        strategyTypeField.onChange(item.id)
      }

      logEvent('Choose type of customer strategy')
    },
    [getValues, index, logEvent, strategyTypeField],
  )

  const handleConfirmDialogCancel = React.useCallback<
    EH<ExecuteCancelDialogProps, 'onCancel'>
  >(() => {
    setNextCustomerStrategyType(undefined)
  }, [])

  const handleConfirmDialogExecute = React.useCallback<
    EH<ExecuteCancelDialogProps, 'onExecute'>
  >(() => {
    setValue(`strategies.${index}.segmentRoutes`, [])
    strategyTypeField.onChange(nextCustomerStrategyType)
    setNextCustomerStrategyType(undefined)
  }, [index, nextCustomerStrategyType, setValue, strategyTypeField])

  return (
    <>
      <ItemSelector
        data={data}
        dataItemKey="id"
        textField="label"
        width={240}
        {...strategyTypeField}
        value={value}
        onChange={handleChange}
      />

      {exists(nextCustomerStrategyType) && (
        <ExecuteCancelDialog
          executeButtonText={t('switch')}
          mode="delete"
          title={t('switch_strategy')}
          onCancel={handleConfirmDialogCancel}
          onExecute={handleConfirmDialogExecute}
        >
          {t('switching_will_reset_growth_paths_are_you_sure')}
        </ExecuteCancelDialog>
      )}
    </>
  )
}
